import React, { createContext, useContext } from "react";
import PropTypes from "prop-types";
//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { useAppVisibilityContext } from "../app-visibility/AppVisibilityContext";
import { useMobileDetectionContext } from "../mobile-detection/MobileDetectionContext";

const ScrollLockContext = createContext({});

export const ScrollLockContextProvider = ({ children }) => {
    const hook = useScrollLock();

    return <ScrollLockContext.Provider value={hook}>{children}</ScrollLockContext.Provider>;
};

export const useScrollLockContext = () => {
    return useContext(ScrollLockContext);
};

ScrollLockContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};

const INITIAL_STATE = {
    hasScrollLock: false,
    clientBodyStyles: {
        position: ""
    }
};

const useScrollLock = () => {
    const [scrollLockState, setScrollLockState] = React.useState(INITIAL_STATE);
    const log = pilog.getLogger("ScrollLockContext useScrollLock");
    const {
        appVisibility: { isAppOpen }
    } = useAppVisibilityContext();
    const { isMobileDevice } = useMobileDetectionContext();

    React.useEffect(() => {
        //keep track of the users scroll event for mobile devices
        if (isMobileDevice) {
            window.addEventListener("scroll", () => {
                try {
                    document.documentElement.style.setProperty("--scroll-y", `${window.scrollY}px`);
                } catch {
                    log.warn("Error tracking scroll position");
                }
            });
        }
    }, [isMobileDevice]);

    React.useEffect(() => {
        if ((!isAppOpen && isMobileDevice) || scrollLockState.hasScrollLock) {
            removeScrollLock();
        }

        if (isAppOpen && isMobileDevice) {
            addScrollLock();
        }
    }, [isAppOpen, isMobileDevice]);

    const addScrollLock = () => {
        const scrollY = document.documentElement.style.getPropertyValue("--scroll-y");
        const body = document.body;
        const clientBodyStyles = {
            position: body.style.position || null
        };
        body.style.position = "fixed";
        body.style.top = `-${scrollY}`;
        setScrollLockState({ clientBodyStyles, hasScrollLock: true });
    };

    const removeScrollLock = () => {
        const body = document.body;
        const scrollY = body.style.top;
        const { position } = scrollLockState.clientBodyStyles;
        body.style.position = position || "";
        body.style.top = "";
        window.scrollTo(0, parseInt(scrollY || "0") * -1);
    };

    return {
        addScrollLock,
        removeScrollLock
    };
};
